/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, SortEntry } from 'app/shared/lib/essette-site';
import { Api, apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IPopup } from 'app/shared/elements/popup-base';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { AuthService } from 'app/um/services/auth-service';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import * as UM from 'app/um/models/essette-api';
import { requiredFields as memberRequiredFields, requiredPlanFields as planRequiredFields } from 'app/cd/elements/members/member-display';

@autoinject()
export class MyAuthsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected auths: ReadonlyArray<UM.Auth> = [];
    protected selectedAuth: UM.Auth | null = null;
    protected selectedAuthId: string | null = null;

    protected hideAuthsInWorkQueues: boolean = false;
    protected loadModal: boolean = false;

    protected hasAuthOwner: boolean = false;
    protected hasAuthRoute: boolean = false;
    protected showAuthSummaryLink: boolean = false;
    protected showProviderSummaryLink: boolean = false;
    protected openAuthSummary = openAuthSummary;
    protected openProviderSummary = openProviderSummary;

    protected defaultSortEntries: SortEntry[] = [];
    protected resultCount: number | null = 0;
    protected pageSize: number = 20;
    private sorting: Sorting = new Sorting();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly api: Api,
        protected service: AuthService,
        ...rest: any[]) {
        super(...rest);
    }

    protected async canActivate(): Promise<boolean> {
        await Promise.all([
            this.checkPermissions(),
            this.loadSettings()
        ]);
        if(!this.hasAuthOwner) this.logger.error('User does not have Auth Owner permission');
        return true;
    }

    protected async activate(): Promise<void> {
        await this.loadMyAuths();
    }

    ////****************** Events ***************************************************////
    protected openQueueHistory(auth: UM.Auth): void {
        this.selectedAuth = auth;
        this.selectedAuthId = auth.id!;
        this.loadModal = true;
        this.$modal.open();
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAuthOwner = this.accountContext.hasPermission('UM.Auth.Duty.Owner');
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('um.auth.summary');
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    protected async reload(): Promise<void> {
        return this.loadMyAuths();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMyAuths(): Promise<void> {
        if(!this.hasAuthOwner) { return; }

        // use default sort if no other sort specified
        let sort = new Sorting();
        if(this.sorting.entries.length === 0 && this.defaultSortEntries?.length) sort.entries.push(...this.defaultSortEntries);
        else sort = this.sorting;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: sort,
            includeInactive: true
        };

        const includes = [
            'class',
            'class.subClass',
            'healthcareServiceType',
            'member',
            'member.plan',
            'member.plan.healthPlan',
            'member.plan.lineOfBusiness',
            'placeOfService',
            'priority',
            'provider.attending',
            'provider.requested',
            'provider.requesting',
            'provider.servicing',
            'queues',
            'status',
            'type'
        ];

        const fields = [
            'class.subClass.description',
            'dueDateTime',
            'healthcareServiceType.description',
            'isClosed',
            'member.eligibility.isCurrent',
            'nextReviewDate',
            'number',
            'placeOfService.description',
            'priority.description',
            'provider.attending.description',
            'provider.attending.name.full',
            'provider.requested.description',
            'provider.requested.name.full',
            'provider.requesting.description',
            'provider.requesting.name.full',
            'provider.servicing.description',
            'provider.servicing.name.full',
            'queues.description',
            'requestedDateTime',
            'status.description',
            'type.description'
        ];
        memberRequiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);
        planRequiredFields.forEach(field => !fields.includes(`member.plan.${field}`) ? fields.push(`member.plan.${field}`) : null);

        const userId = this.env.user.id;
        if(!userId) { return; }

        try {
            this.state.beginLoad();

            const query = this.api.query('um/auths')
                .strategy('owner')
                .applyStandardLoadOptions(options)
                .include(...includes)
                .field(...fields)
                .filterIdentifier('assignedTo.id', userId)
                .filterBoolean('isClosed', false);

            if(this.hideAuthsInWorkQueues) {
                query.filterNot(not => {
                    not.filterExists('queues');
                });
            }

            const response: IApiQueryResults<UM.Auth> = await this.api.get(query);
            this.auths = response.results;
            this.resultCount = response.count;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadSettings(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.service.loadAuthSettings();
            const settings = response.settings;

            // attempt to configure default sort via attribute
            const defaultSort = getApiSetting(settings, 'defaultMyAuthsSort.$default')?.toString();
            if(!defaultSort) return; // no attribute value found

            const [reference, directionString]: string[] = defaultSort.split(':');
            if(!reference || !['asc', 'desc'].includes(directionString)) return; // attribute value is invalid

            this.defaultSortEntries.push({
                reference,
                direction: directionString === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        } catch(error) {
            throw error;
        } finally {
            // if default sort not configured via attribute, use fallback
            if(!this.defaultSortEntries.length) {
                this.defaultSortEntries.push({
                    reference: 'dueDateTime',
                    direction: SortDirection.Ascending
                });
                this.defaultSortEntries.push({
                    reference: 'number',
                    direction: SortDirection.Ascending
                });
            }
            this.state.endLoad();
        }
    }
}
